"use client";

import { FC, useState } from "react";
import { Popover } from "antd";
import { Icon, OspToast } from "@ospweb/sdk-ui";
import { cn } from "utils";
import Translations from "@translate/components/Member/CurrentPlan/ManageBillingModal/SubscriptionRowActions";
import { I18nUtil } from "@utils/i18n";
import { ShowOnPcOrMobile } from "@components/ShowOnPcOrMobile";
import { useUpdateAutoRenew } from "@hooks/useSubscriptionManage";
import type {
  SubscriptionAction,
  SubscriptionActions,
} from "./subscriptionActions";
import type { SubscriptionListItem, SubscriptionListProps } from "./types";

const I18n = I18nUtil.createScoped(Translations);

/** 与旧渲染同一套 Popover 外观（`SubscriptionList` 现有菜单） */
const POPOVER_CLASS_NAMES = {
  root: "rounded-2 border-solid border border-border_solid insetShadowMd",
  // !p-2：覆盖 antd 浮层自带内边距（原写法 styles.body.padding: 8）
  body: "!bg-background_solid_neutral_secondary !p-2",
};
const POPOVER_STYLES = { root: { padding: 0 } };
const MENU_ITEM_CLASS =
  "body_medium_m w-full p-2 gap-2 flex flex-row items-center text-content_primary disabled:text-content_quaternary group";
const ROW_BUTTON_CLASS =
  "button_s xs_button button secondary_button min-w-20 md:min-w-28";

const actionLabel = (action: SubscriptionAction): string => {
  switch (action) {
    case "turn_off":
      return I18n.t("Turn off auto-renew");
    case "resume":
      return I18n.t("Resume auto-renew");
    case "update_pm":
      return I18n.t("Update payment method");
    case "manage_portal":
      return I18n.t("Manage");
    default:
      return I18n.t("Manage on edwealth.ai");
  }
};

type RowHandlers = Pick<
  SubscriptionListProps,
  "onCancel" | "onManageSubscription" | "onUpdatePaymentMethod"
>;

interface SubscriptionRowActionsProps extends RowHandlers {
  item: SubscriptionListItem;
  actions: SubscriptionActions;
  position: "pc_modal" | "h5_page";
}

interface ActionButtonProps {
  action: SubscriptionAction;
  item: SubscriptionListItem;
  position: "pc_modal" | "h5_page";
  className: string;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * 一个可点动作；label 写成字面量三元 —— 埋点生成器按属性表达式文本收录，变量 / 查表会被误解析。
 * 嵌套三元豁免（OCR Ternary Expressions）：只此一处，原因同上。
 */
const ActionButton: FC<ActionButtonProps> = ({
  action,
  item,
  position,
  className,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    data-openreplay-label={
      action === "turn_off"
        ? "member_subscriptionlist_turn_off"
        : action === "resume"
          ? "member_subscriptionlist_resume"
          : action === "update_pm"
            ? "member_subscriptionlist_update_pm"
            : "member_subscriptionlist_manage_portal"
    }
    // 字面量：禁用的按钮浏览器不派发 click，不会虚增；条件写法生成器收录不到
    data-or-event="subscription_action_click"
    data-or-param-action={action}
    data-or-param-planid={item.id}
    data-or-param-channel={item.pay_channel}
    data-or-param-position={position}
    disabled={disabled}
    onClick={onClick}
    className={className}
  >
    {action === "turn_off" && (
      <Icon
        name="close"
        size={14}
        className="text-content_primary !inline-flex border rounded-full size-3.75 items-center justify-center border-content_primary group-disabled:border-content_quaternary group-disabled:text-content_quaternary"
      />
    )}
    {actionLabel(action)}
  </button>
);

/** 外跳不可用时的不可点提示（旧版 App / 远程降级 / 未知渠道） */
const ManageOnWebHint: FC<{ className: string }> = ({ className }) => (
  // or-label: n/a —— 纯提示文本，不可点击
  <p className={cn(className, "text-content_tertiary cursor-default")}>
    {actionLabel("manage_on_web")}
  </p>
);

/** 行主动作：主动作可用则用之；否则菜单里还有可用项就退为「打开菜单」；都没有则不渲染 */
function resolvePrimary(
  actions: SubscriptionActions,
  isAvailable: (action: SubscriptionAction) => boolean,
): SubscriptionAction | "menu" | null {
  if (
    actions.primary &&
    actions.primary !== "menu" &&
    isAvailable(actions.primary)
  ) {
    return actions.primary;
  }
  return actions.menu.some(isAvailable) ? "menu" : null;
}

/**
 * 按能力渲染的订阅行操作（EDG-552，fe-spec §4.6 F5）。形态沿用两端现有交互：
 * H5 =「…」Popover 列出全部动作；PC = 行内按钮显示主动作，主动作为菜单时弹出同款 Popover。
 */
export const SubscriptionRowActions: FC<SubscriptionRowActionsProps> = ({
  item,
  actions,
  position,
  onCancel,
  onManageSubscription,
  onUpdatePaymentMethod,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { updateAutoRenew, isUpdating } = useUpdateAutoRenew();

  // 换卡入口由 Slice 5 接入；未接线时不渲染，避免出现点了没反应的按钮
  const isAvailable = (action: SubscriptionAction) =>
    action !== "update_pm" || !!onUpdatePaymentMethod;
  const primary = resolvePrimary(actions, isAvailable);
  const menu = actions.menu.filter(isAvailable);

  const run = async (action: SubscriptionAction) => {
    setIsMenuOpen(false);
    switch (action) {
      case "turn_off":
        onCancel?.(item);
        return;
      case "resume": {
        const updated = await updateAutoRenew(item, true);
        if (updated) {
          OspToast.success({ content: I18n.t("Auto-renew resumed.") });
        }
        return;
      }
      case "update_pm":
        onUpdatePaymentMethod?.(item);
        return;
      case "manage_portal":
        onManageSubscription?.(item);
        return;
      default:
        return;
    }
  };

  const renderItem = (action: SubscriptionAction) =>
    action === "manage_on_web" ? (
      <ManageOnWebHint key={action} className={MENU_ITEM_CLASS} />
    ) : (
      <ActionButton
        key={action}
        action={action}
        item={item}
        position={position}
        className={MENU_ITEM_CLASS}
        disabled={isUpdating}
        // or-label: n/a —— label 与业务事件打在 ActionButton 内部按钮上
        onClick={() => void run(action)}
      />
    );

  const menuContent = (actionsInMenu: SubscriptionAction[]) => (
    <div
      data-or-view="subscription_surface_view"
      data-or-param-surface="manage_menu"
      data-or-param-planid={item.id}
      data-or-param-channel={item.pay_channel}
    >
      {actionsInMenu.map(renderItem)}
    </div>
  );

  const popoverProps = {
    trigger: "click" as const,
    placement: "bottomRight" as const,
    arrow: false,
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    classNames: POPOVER_CLASS_NAMES,
    styles: POPOVER_STYLES,
  };

  // H5：主动作排在菜单第一项
  const mobileItems = [
    ...(primary && primary !== "menu" ? [primary] : []),
    ...menu,
  ];

  /** 「…」更多按钮：H5 承载全部动作；PC 在主按钮为具体动作时承载其余动作（F5） */
  const renderMoreTrigger = (actionsInMenu: SubscriptionAction[]) => (
    <Popover content={menuContent(actionsInMenu)} {...popoverProps}>
      <button
        type="button"
        data-openreplay-label="member_moreactions_click"
        className="relative shrink-0 rounded-full flex items-center justify-center"
        aria-label="More actions"
      >
        <Icon
          name="dots-horizontal-rounded"
          size={20}
          className="text-content_primary hover:opacity-60 transition-opacity"
        />
      </button>
    </Popover>
  );

  /**
   * PC：主动作为菜单 →「Manage」打开菜单；为具体动作 → 行内按钮 + 有其余动作时追加「…」
   * （扣款失败仍可关续费、已关续费仍可换卡，F5 / Review Issue #3）。
   */
  const renderPcActions = () => {
    if (primary === "menu") {
      return (
        <Popover content={menuContent(menu)} {...popoverProps}>
          <button
            type="button"
            data-openreplay-label="member_subscriptionlist_primary"
            className={ROW_BUTTON_CLASS}
          >
            {I18n.t("Manage")}
          </button>
        </Popover>
      );
    }
    if (primary === "manage_on_web") {
      return <ManageOnWebHint className="body_s" />;
    }
    if (!primary) {
      return null;
    }
    return (
      <>
        <ActionButton
          action={primary}
          item={item}
          position={position}
          className={ROW_BUTTON_CLASS}
          disabled={isUpdating}
          // or-label: n/a —— label 与业务事件打在 ActionButton 内部按钮上
          onClick={() => void run(primary)}
        />
        {menu.length > 0 && renderMoreTrigger(menu)}
      </>
    );
  };

  return (
    <>
      <ShowOnPcOrMobile showMode="mobile">
        {mobileItems.length > 0 && renderMoreTrigger(mobileItems)}
      </ShowOnPcOrMobile>
      <ShowOnPcOrMobile showMode="pc">
        <div className="flex items-center gap-2 shrink-0 mr-px">
          {renderPcActions()}
        </div>
      </ShowOnPcOrMobile>
    </>
  );
};
